import React from "react";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import TextArea from "../../components/form/input/TextArea";
import Select from "../../components/form/Select";
import Switch from "../../components/form/switch/Switch";
import { SubscriptionPlanFormData, SubscriptionPlanFormErrors } from "./types";

interface SubscriptionPlanFormProps {
  data: SubscriptionPlanFormData;
  errors: SubscriptionPlanFormErrors;
  isLoading: boolean;
  submitLabel: string;
  loadingLabel: string;
  onChange: (field: keyof SubscriptionPlanFormData, value: string | boolean) => void;
  onCancel: () => void;
  onSubmit: () => void;
}

const billingCycleOptions = [
  { value: "month", label: "Monthly" },
  { value: "year", label: "Yearly" },
];

export default function SubscriptionPlanForm({
  data,
  errors,
  isLoading,
  submitLabel,
  loadingLabel,
  onChange,
  onCancel,
  onSubmit,
}: SubscriptionPlanFormProps) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <Label>Plan Name</Label>
          <Input
            value={data.name}
            onChange={(event) => onChange("name", event.target.value)}
            placeholder="Enter plan name"
            disabled={isLoading}
            error={Boolean(errors.name)}
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        <div>
          <Label>Price</Label>
          <Input
            type="number"
            min="0"
            step={0.01}
            value={data.price}
            onChange={(event) => onChange("price", event.target.value)}
            placeholder="0.00"
            disabled={isLoading}
            error={Boolean(errors.price)}
          />
          {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price}</p>}
        </div>

        <div>
          <Label>Currency</Label>
          <Input
            value={data.currency}
            onChange={(event) => onChange("currency", event.target.value)}
            placeholder="usd"
            disabled={isLoading}
            error={Boolean(errors.currency)}
          />
          {errors.currency && <p className="mt-1 text-sm text-red-600">{errors.currency}</p>}
        </div>

        <div>
          <Label>Billing Cycle</Label>
          <Select
            key={data.interval}
            options={billingCycleOptions}
            defaultValue={data.interval}
            onChange={(value) => onChange("interval", value)}
            placeholder="Select billing cycle"
            className={errors.interval ? "border-error-500" : ""}
          />
          {errors.interval && <p className="mt-1 text-sm text-red-600">{errors.interval}</p>}
        </div>
      </div>

      <div>
        <Label>Description</Label>
        <TextArea
          value={data.description}
          onChange={(value) => onChange("description", value)}
          rows={4}
          placeholder="Enter plan description"
          disabled={isLoading}
          error={Boolean(errors.description)}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <Label>Apple Product ID</Label>
          <Input
            value={data.appleProductId}
            onChange={(event) => onChange("appleProductId", event.target.value)}
            placeholder="com.Viyume.flashcard.pro.monthly"
            disabled={isLoading}
          />
        </div>

        <div>
          <Label>Google Product ID</Label>
          <Input
            value={data.googleProductId}
            onChange={(event) => onChange("googleProductId", event.target.value)}
            placeholder="com.Viyume.flashcard.pro.monthly"
            disabled={isLoading}
          />
        </div>
      </div>

      <div>
        <Label>Limits</Label>
        <TextArea
          value={data.featuresText}
          onChange={(value) => onChange("featuresText", value)}
          rows={6}
          placeholder={"One feature limit per line"}
          disabled={isLoading}
        />
      </div>

      <div className="flex flex-wrap items-center gap-8">
        <Switch
          key={`active-${data.isActive}`}
          label="Active"
          defaultChecked={data.isActive}
          disabled={isLoading}
          onChange={(checked) => onChange("isActive", checked)}
        />
        <Switch
          key={`popular-${data.isPopular}`}
          label="Popular"
          defaultChecked={data.isPopular}
          disabled={isLoading}
          onChange={(checked) => onChange("isPopular", checked)}
          color="gray"
        />
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60"
          onClick={onSubmit}
          disabled={isLoading}
        >
          {isLoading ? loadingLabel : submitLabel}
        </button>
      </div>
    </div>
  );
}
